import { clsx, type ClassValue } from 'clsx';

/**
 * Combines multiple class names into a single string, supporting conditional
 * class names and objects.
 *
 * > **NOTE**: This utility function is a wrapper around `clsx` and can be extended
 * in the future to include additional functionality, such as `twMerge`, without
 * requiring changes to every component in the project.
 *
 * @param inputs - A list of class values, which can include strings, arrays, objects, or falsy values.
 * @returns A single string of combined class names.
 *
 * @example
 * // Basic usage with strings
 * cn('btn', 'btn-primary'); // 'btn btn-primary'
 *
 * @example
 * // Conditional class names
 * cn('btn', isActive && 'btn-active'); // 'btn btn-active' (if isActive is true)
 *
 * @example
 * // Using objects for conditional classes
 * cn('btn', { 'btn-disabled': isDisabled }); // 'btn btn-disabled' (if isDisabled is true)
 *
 * @example
 * // Combining multiple types
 * cn('btn', ['btn-large', isPrimary && 'btn-primary'], { 'btn-disabled': isDisabled });
 * // 'btn btn-large btn-primary btn-disabled' (if isPrimary and isDisabled are true)
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}
